/**
 * Follow-ups / registrations history — the same four columns on the Dashboard, Sent follow-ups and
 * Meeting registrations pages. Each cell is a primary line plus a dimmed secondary line, so nothing is
 * clamped and no column is narrower than its content. No user ids.
 */
import { useMemo } from 'react';
import { IconLink, IconWorld } from '@tabler/icons-react';
import { Text, Badge, Stack, Group, EmptyState } from '@/components/ui';
import { DataTable, type DataTableColumn } from '@/components/advanced';
import type { AnatomyActivityItem } from '@actschurch/ccms-sdk';
import { stampToDate } from '../utils/format';
import { ResultBadge } from './ResultBadge';
import { originOf, isExternal } from '../utils/options';

type Row = AnatomyActivityItem & { id: number };

export function WhenCell({ stamp }: { stamp?: number | null }) {
  const d = stampToDate(stamp);
  if (!d) return <Text size="sm" c="dimmed">—</Text>;
  return (
    <Stack gap={0}>
      <Text size="sm">{d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</Text>
      <Text size="xs" c="dimmed">{d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</Text>
    </Stack>
  );
}

export function ActivityTable({ items, kind, loading, emptyTitle, emptyDescription, compact = false }: {
  items: AnatomyActivityItem[]; kind: 'follow-up' | 'registration'; loading?: boolean; emptyTitle: string; emptyDescription: string; compact?: boolean;
}) {
  const rows: Row[] = useMemo(() => items.map((i) => ({ ...i, id: i.logId })), [items]);
  const columns: DataTableColumn<Row>[] = [
    { key: 'logDateTime', label: 'When', width: 150, sortable: true, render: (v) => <WhenCell stamp={v} /> },
    {
      key: 'subjectName', label: 'Person', sortable: true,
      render: (v, r) => (
        <Stack gap={2}>
          <Group gap={6} wrap="nowrap">
            <Text size="sm" fw={600}>{v ?? '—'}</Text>
            {r.memberID ? <Badge size="xs" variant="light" color="blue" leftSection={<IconLink size={10} />}>member</Badge> : null}
            {isExternal(r.userId) ? <Badge size="xs" variant="light" color="grape" leftSection={<IconWorld size={10} />}>via {originOf(r.userId)}</Badge> : null}
          </Group>
          <Text size="xs" c="dimmed">{r.anatomyUserId ? `Anatomy #${r.anatomyUserId}` : 'Not yet matched in Anatomy'}</Text>
        </Stack>
      ),
    },
    {
      key: 'referenceName', label: kind === 'follow-up' ? 'Follow-up type' : 'Meeting', sortable: true,
      render: (v, r) => <Stack gap={2}><Text size="sm">{v ?? (r.referenceId ? `#${r.referenceId}` : '—')}</Text>{v && r.referenceId ? <Text size="xs" c="dimmed">#{r.referenceId}</Text> : null}</Stack>,
    },
    {
      key: 'success', label: 'Result', width: compact ? 130 : 260,
      render: (_, r) => (
        <Stack gap={4}>
          <ResultBadge success={r.success} queued={r.queued} message={r.message} />
          {!compact && (r.queued || !r.success) && r.message && <Text size="xs" c="dimmed" style={{ whiteSpace: 'normal' }}>{r.message}</Text>}
        </Stack>
      ),
    },
  ];
  if (!loading && rows.length === 0) return <EmptyState title={emptyTitle} description={emptyDescription} size="sm" />;
  return <DataTable<Row> enableInternalSearch={false} data={rows} columns={columns} loading={loading} striped highlightOnHover enableInternalSort emptyMessage={emptyTitle} />;
}
